import { AngularFirestore, AngularFirestoreCollection, QueryFn } from "@angular/fire/firestore"
import { Alert, AlertData, AlertMain, ManagerFeedbackUnresolved } from "@tr/models"

export class ManagerFeedbackService {
  public alertData: AlertData
  constructor(
    private afs: AngularFirestore,
    public uid: string,
    ) {
    this.alertData = {
      falseFailed: [] as Alert[],
      captainAlerts: [] as Alert[],
      falsePassed: [] as Alert[],
      falseBlocked: [] as Alert[],
      unsolvedFeedback: [] as ManagerFeedbackUnresolved[],
    } as AlertData
  }

  public getAlertMainCollection(): AngularFirestoreCollection {
    return this.afs.collection<AlertMain>("alerts")
  }

  public getAlertCollectionByCompany(alertCompanyName: string): AngularFirestoreCollection {
    return this.afs
    .collection("alerts")
    .doc(alertCompanyName)
    .collection("alerts")
  }

  public getTesterCollection(): AngularFirestoreCollection {
    return this.afs
    .collection("Seva Testers")
  }

  public getFeedbackCollectionByUser(uid: string): AngularFirestoreCollection {
    return this.afs
    .collection("Seva Testers")
    .doc(uid)
    .collection("Feedback")
  }

  private async getTesterName(testerId: string): Promise<string> {
    let data: any
    if ( testerId && testerId !== "") {
      const sevaTesterRef = await this.getTesterCollection()
      .doc(testerId)
      .ref.get()
      data = sevaTesterRef.data()
    }
    return data ? data.name : ""
  }

  private getPlatform(platformsData): string {
    let platform: string = platformsData
    if (platformsData.includes("iOS") || platformsData.includes("Android")) {
      const platformOS = platformsData.split(" os:")
      platform = platformOS[0]
    }
    return platform
  }

  private async deleteAlertsByPlatform(reportersData, alertDataItem, platform): Promise<void> {
    if (
      !reportersData.managerStatus ||
      reportersData.managerStatus.length === 0 ||
      alertDataItem.correctStatus ===
      reportersData.managerStatus
    ) {
      const platformRef = await this.afs
        .collection("alerts")
        .doc(alertDataItem.company)
        .collection("alerts")
        .ref
        .where("company", "==", alertDataItem.company)
        .where("project", "==", alertDataItem.project)
        .where("runId", "==", alertDataItem.runId)
        .where("testId", "==", alertDataItem.testId)
        .where("input", "==", alertDataItem.input)
        .where(
          "dateFlagged",
          "==",
          alertDataItem.dateFlagged,
        )
        .where("platforms", "==", platform)
        .get()
      const alertDeleteActions = platformRef.docs
        .filter(doc => doc.data().platforms === platform)
        .map(async doc => {
          return this.afs
            .collection("alerts")
            .doc(alertDataItem.company)
            .collection("alerts")
            .doc(doc.id)
            .delete()
        })
      await Promise.all(alertDeleteActions)
    }
  }
  private captainUpdatedToIncorrectStatus(reportersData,alertDataItem,statusData): boolean {
    return reportersData.managerStatus
    && reportersData.managerStatus.length > 0
    && alertDataItem.managerReviewed
    && reportersData.manager === this.uid
    && alertDataItem.correctStatus !== statusData.managerStatus
  }

  private async setupAlerts(reportersData, alertDataItem, id): Promise<void> {
    try {
      if (reportersData.platform) {
        // checking with os platform
        const platform = this.getPlatform(alertDataItem.platforms)
        // find tester and tester mistake if exists
        if (platform === reportersData.platform &&
          (reportersData.status !== alertDataItem.correctStatus ||
            (reportersData.managerStatus
              && reportersData.managerStatus.length > 0
              && alertDataItem.correctStatus !== reportersData.managerStatus))) {
          const testerMistake = status !== alertDataItem.correctStatus
          const [ manager, reporter ] = await Promise.all([
            this.getTesterName(reportersData.manager),
            this.getTesterName(reportersData.reporter),
          ])

          const managerStatus = reportersData.managerStatus

          const statusData = {
            manager,
            managerStatus,
            status: reportersData.status,
            reporter,
            testerMistake,
          }
          // Don't add to tester errors if the manager incorrectly marked the test as failed when it was passed

          if (this.captainUpdatedToIncorrectStatus(reportersData,alertDataItem,statusData)) {
            this.alertData.captainAlerts.push(this.generateAlert(alertDataItem,statusData,"Manager", id))
          }

          if (reportersData.status === "Passed") {
            this.alertData.falsePassed.push(this.generateAlert(alertDataItem, statusData, "Passed", id))
          } else if (reportersData.status === "Failed") {
            this.alertData.falseFailed.push(this.generateAlert(alertDataItem, statusData, "Failed", id))
          } else if ( reportersData.status === "Blocked") {
            this.alertData.falseBlocked.push(this.generateAlert(alertDataItem, statusData, "Blocked", id))
          } else if (
            platform === reportersData.platform &&
            alertDataItem.correctStatus === reportersData.status
          ) {
            await this.deleteAlertsByPlatform(reportersData, alertDataItem, platform)
          }
        }
      }
    } catch (err) {
      console.error(err)
    }
  }

  private async setupFeedback(): Promise<void> {
    const snapshots = await this.getTesterCollection().ref.get()
    if (!snapshots.empty) {
      const feedbacks = []
      snapshots.forEach(feedback => feedbacks.push(feedback.data()))
      const feedbackActionPromises = feedbacks
      .filter(data => data.uid)
      .map(async data => {
        const feedbackSnapshots = await this.getFeedbackCollectionByUser(data.uid).ref.get()
        return Promise.all(feedbackSnapshots.docs.map(async feedbackDoc => {
          if (feedbackDoc.exists) {
            const feedbackDataItem = feedbackDoc.data()
            if (!feedbackDataItem.isResolved) {
              const reporter = await this.getTesterName(feedbackDataItem.reporterId)
              this.alertData.unsolvedFeedback.push({
                _id: feedbackDataItem.id,
                testId: feedbackDataItem.testId,
                testType: feedbackDataItem.testType,
                company: feedbackDataItem.company,
                platforms: feedbackDataItem.platform,
                actualStatus: feedbackDataItem.actualStatus,
                markedStatus: feedbackDataItem.markedStatus,
                testRun: feedbackDataItem.testRun,
                reporter,
                environment: feedbackDataItem.environment,
              } as ManagerFeedbackUnresolved)
            }
          }
        }))
      })
      await Promise.all(feedbackActionPromises)
    }
  }

  private generateAlert(alertDataItem, statusData, alertType, id): Alert {
    return {
      _id: id,
      alerter: "tester",
      alertType,
      dateFlagged: alertDataItem.dateFlagged.toDate(),
      manager: statusData.manager,
      testId: alertDataItem.testId,
      company: alertDataItem.company,
      input: alertDataItem.input,
      platforms: alertDataItem.platforms,
      status: statusData.managerStatus,
      correctStatus: alertDataItem.correctStatus,
      reportedStatus: statusData.status,
      runId: alertDataItem.runId,
      reporter: statusData.reporter,
      reporterManager: statusData.manager,
      testerMistake: statusData.testerMistake,
      project: alertDataItem.project,
    } as Alert
  }

  private async setupAlertData(): Promise<void> {
    const snapshots = await this.getAlertMainCollection().ref.get()
    if (!snapshots.empty) {
      const alerts = []
      snapshots.forEach(alert => alerts.push(alert.data()))
      const alertActionPromises = alerts.map(async data => {
        const alertDataSnapShot = await this.getAlertCollectionByCompany(data.name).ref.get()
        await Promise.all(alertDataSnapShot.docs.map(async alertDoc => {
          if (alertDoc.exists) {
            const alertDataItem = alertDoc.data()
            if (alertDataItem.reporters && alertDataItem.reporters.length > 0) {
              await Promise.all(alertDataItem.reporters.map( async reporterItem => {
                await this.setupAlerts(reporterItem, alertDataItem, alertDoc.id)
              }))
            }
          }
        }))
      })

      await Promise.all(alertActionPromises)
    }
  }

  public async setup(): Promise<void> {
    await Promise.all([
      this.setupAlertData(),
      this.setupFeedback(),
    ])
  }
}
